import { SelectOptionWithIcon } from '@/components/formBuilder/SelectComponents/SelectOptionWithIcon';
import { useMemo } from 'react';
import { useGetMeetingLocationsAvailability } from '@/api';
import {
  type MeetingLocationOption,
  meetingLocationUiOptions,
} from './meetingLocationUiOptions';

interface MeetingLocationConfig {
  name?: string;
  label?: string;
  defaultValue?: string;
  disabled?: boolean;
  options?: MeetingLocationOption[];
}

type MeetingLocationConfigProps = {
  config?: Partial<MeetingLocationConfig>;
  isHackerRank?: boolean;
};

export const useMeetingLocationConfig = ({
  config,
  isHackerRank = false,
}: MeetingLocationConfigProps = {}) => {
  const { name = 'location', ...overRides } = config ?? {};

  const { locations: locationsAvailability = {} } =
    useGetMeetingLocationsAvailability();

  const options = useMemo<MeetingLocationOption[]>(() => {
    if (!meetingLocationUiOptions?.length) return [];
    return meetingLocationUiOptions
      .map((option) => ({
        ...option,
        isAvailable: Boolean(locationsAvailability?.[option?.value]),
      }))
      .filter((option) => option.isAvailable);
  }, [locationsAvailability]);

  const defaultLocation = useMemo(() => {
    if (!options?.length) return undefined;
    if (isHackerRank) return 'hackerrank';
    const isZoomAvailable = options?.some((item) => item.value === 'zoom');
    return isZoomAvailable ? 'zoom' : 'onsite';
  }, [options, isHackerRank]);

  const formConfig = useMemo(
    () => ({
      name,
      label: 'Location',
      type: 'select',
      isRequired: false,
      defaultValue: defaultLocation,
      width: '100%',
      customFormatLabel: SelectOptionWithIcon,
      options,
      placeholder: 'Select location',
      menuPlacement: 'top',
      disabled: isHackerRank,
      ...overRides,
    }),
    [name, options, overRides, isHackerRank, defaultLocation]
  );

  return {
    formConfig,
    options,
    locationsAvailability,
    defaultLocation,
  };
};
